import { WORKFLOW_RUN_NOTICE_THREAD_PREFIX } from "@amoreg/contracts";
import { notificationPrimaryDestination } from "./inboxDetailActions";
import { NotificationDestinationLink, NotificationRunLink } from "./NotificationRunLink";
import { inboxItemTitle, notificationSourceLabel } from "./inboxPresentation";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, FileTextIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import type { InboxItem } from "../../state/inbox";
import { useActiveOrgId } from "../../state/orgs";
import { Avatar, AvatarFallback, personInitials } from "../ui/avatar";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { INBOX_SEVERITY } from "./inboxPresentation";
import { LabelChip } from "../ui/label-chip";

/** The severity dots use the app's own status reds, ambers and blues. */
const SEVERITY_DOT = {
  high: "var(--destructive-foreground)",
  medium: "var(--warning-foreground)",
  low: "var(--info-foreground)",
} as const;

/** The contextual sections below an update's properties, shared by desktop and mobile. */
export function InboxContextPanel({
  item,
  related,
  onOpen,
  onFilter,
}: {
  item: InboxItem;
  related: readonly InboxItem[];
  onOpen: (item: InboxItem) => void;
  onFilter: (filter: {
    kind?: InboxItem["kind"];
    jurisdiction?: string;
    query?: string;
    severity?: "high" | "medium" | "low";
  }) => void;
}) {
  const activeOrgId = useActiveOrgId();
  if (item.kind === "agent-notification")
    return (
      <div className="mt-8 space-y-7">
        <ContextSection title="Labels">
          <div className="flex flex-wrap gap-1.5">
            <LabelChip
              name={notificationSourceLabel(item.source)}
              onClick={() => onFilter({ kind: "agent-notification", query: item.source })}
            />
            <LabelChip
              name={INBOX_SEVERITY[item.severity].label}
              color={SEVERITY_DOT[item.severity]}
              onClick={() => onFilter({ severity: item.severity })}
            />
          </div>
        </ContextSection>
        <ContextSection title="Source run">
          {item.threadId.startsWith(WORKFLOW_RUN_NOTICE_THREAD_PREFIX) && notificationPrimaryDestination(item) ? (
            <NotificationDestinationLink item={item} destination={notificationPrimaryDestination(item)!} variant="outline" />
          ) : (
            <NotificationRunLink item={item} />
          )}
        </ContextSection>
      </div>
    );
  if (item.kind === "workflow-approval")
    return (
      <div className="mt-8 space-y-7">
        <ContextSection title="Labels">
          <div className="flex flex-wrap gap-1.5">
            <LabelChip
              name="Approval"
              onClick={() => onFilter({ kind: "workflow-approval" })}
              aria-label="Filter by approvals"
            />
            <LabelChip
              name={INBOX_SEVERITY.high.label}
              color={SEVERITY_DOT.high}
              onClick={() => onFilter({ severity: "high" })}
            />
          </div>
        </ContextSection>
        <ContextSection title="Workflow">
          <Button
            variant="ghost"
            size="sm"
            className="-mx-1.5 h-auto! w-[calc(100%+0.75rem)] items-start justify-start gap-2.5 whitespace-normal px-1.5 py-1.5 text-left font-normal"
            render={
              <Link
                to="/$org/workflows/$workflowId"
                params={{ org: orgUrlSegment(item.organizationId), workflowId: item.workflowId }}
                search={{ run: item.runId, step: item.nodeId }}
              />
            }
            title="Open the run's chart"
            data-inbox-approval-open=""
          >
            <span className="min-w-0 flex-1 text-[13px] leading-5">
              <span className="block">{item.workflowName}</span>
              <span className="block text-[11px] text-muted-foreground">Open the run's chart</span>
            </span>
            <ArrowUpRightIcon className="mt-1 size-3 shrink-0 text-muted-foreground/60" />
          </Button>
        </ContextSection>
      </div>
    );
  const review = item.kind === "policy-review";
  return (
    <div className="mt-8 space-y-7">
      <ContextSection title="Labels">
        <div className="flex flex-wrap gap-1.5">
          <LabelChip
            name={review ? "Policy review" : "Invitation"}
            onClick={() => onFilter({ kind: item.kind })}
            aria-label={`Filter by ${review ? "policy reviews" : "invitations"}`}
          />
          {review ? (
            <LabelChip
              name={INBOX_SEVERITY[item.severity].label}
              color={SEVERITY_DOT[item.severity]}
              onClick={() => onFilter({ severity: item.severity })}
              aria-label={`Filter by ${INBOX_SEVERITY[item.severity].label}`}
            />
          ) : null}
          <LabelChip
            name={review ? item.jurisdiction : item.role}
            onClick={() =>
              onFilter(
                review
                  ? { jurisdiction: item.jurisdiction }
                  : { kind: "invitation", query: item.role },
              )
            }
            aria-label={`Filter by ${review ? item.jurisdiction : item.role}`}
          />
        </div>
      </ContextSection>
      <ContextSection title={review ? "Regulatory source" : "Organization"}>
        <Button
          variant="ghost"
          size="sm"
          className="-mx-1.5 h-auto! w-[calc(100%+0.75rem)] items-start justify-start gap-2.5 whitespace-normal px-1.5 py-1.5 text-left font-normal"
          onClick={
            review
              ? undefined
              : () => onFilter({ kind: "invitation", query: item.organizationName })
          }
          render={
            review && activeOrgId ? (
              <Link to="/$org/compliance/regulatory" params={{ org: orgUrlSegment(activeOrgId) }} />
            ) : undefined
          }
          disabled={review && !activeOrgId}
          title={review ? "Open regulatory monitoring" : "Show invitations from this organization"}
        >
          {review ? (
            <FileTextIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <Avatar className="size-6">
              <AvatarFallback className="text-[9px]">
                {personInitials({ name: item.organizationName })}
              </AvatarFallback>
            </Avatar>
          )}
          <span className="min-w-0 flex-1 text-[13px] leading-5">
            <span className="block">
              {review ? item.regulationTitle.split(" — ")[0] : item.organizationName}
            </span>
            <span className="block text-[11px] text-muted-foreground">
              {review ? item.jurisdiction : `Invited as ${item.role}`}
            </span>
          </span>
          <ArrowUpRightIcon className="mt-1 size-3 shrink-0 text-muted-foreground/60" />
        </Button>
      </ContextSection>
      {related.length > 0 && (
        <ContextSection title="Related updates" count={related.length}>
          <ul className="-mx-1.5 space-y-1">
            {related.map((entry) => {
              const title = inboxItemTitle(entry);
              return (
                <li key={`${entry.kind}:${entry.id}`}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onOpen(entry)}
                          aria-label={`Open update: ${title}`}
                          className="h-auto! w-full justify-start gap-2 rounded-md px-1.5 py-1.5 text-xs font-normal sm:text-xs"
                        >
                          <span
                            className={cn(
                              "size-1.5 shrink-0 rounded-full",
                              entry.kind === "policy-review"
                                ? INBOX_SEVERITY[entry.severity].dotClass
                                : "bg-success-foreground",
                            )}
                          />
                          <span className="truncate">{title}</span>
                        </Button>
                      }
                    />
                    <TooltipPopup side="left" className="max-w-64">
                      {title}
                    </TooltipPopup>
                  </Tooltip>
                </li>
              );
            })}
          </ul>
        </ContextSection>
      )}
    </div>
  );
}

function ContextSection({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-[13px] font-medium text-muted-foreground">{title}</h3>
        {count !== undefined && (
          <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
        )}
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </section>
  );
}
